import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Calendar, MapPin, Users, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const fmtHM = (iso) => {
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

function relativeDate(iso) {
  const d = new Date(iso);
  const now = new Date();
  const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((dOnly - nowOnly) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Demain';
  if (diff > 1 && diff < 7) return d.toLocaleDateString('fr-FR', { weekday: 'long' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export default function UpcomingEventsWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState('team');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdminManager = user && (user.role === 'admin' || user.role === 'owner' || user.role === 'manager');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/agenda/upcoming', {
          params: { limit: 5, scope },
        });
        setEvents(r.data.events || []);
      } catch (e) {
        // module can be disabled for this user – silently ignore
        setEvents([]);
      } finally { setLoading(false); }
    })();
  }, [scope]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="dash-upcoming-events">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-blue-600" />
          </div>
          <h3 className="font-semibold text-slate-900 text-sm">Prochains événements</h3>
        </div>
        <div className="flex items-center gap-2">
          {isAdminManager && (
            <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="dash-upcoming-scope">
              {[['team', "Équipe"], ['mine', 'Moi']].map(([v, l]) => (
                <button key={v}
                        onClick={() => setScope(v)}
                        data-testid={`dash-upcoming-scope-${v}`}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                          scope === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                        }`}>{l}</button>
              ))}
            </div>
          )}
          <button
            onClick={() => navigate('/agenda')}
            data-testid="dash-upcoming-see-all"
            className="text-xs font-medium text-blue-700 hover:text-blue-900 flex items-center gap-0.5"
          >
            Voir tout <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
        </div>
      ) : events.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-500">
          Aucun événement à venir.<br />
          <button className="text-blue-700 hover:text-blue-900 font-medium mt-1"
                   onClick={() => navigate('/agenda')}>Ouvrir l'agenda →</button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="dash-upcoming-list">
          {events.map((ev) => (
            <li key={ev.event_id}
                onClick={() => navigate('/agenda')}
                data-testid={`dash-upcoming-item-${ev.event_id}`}
                className="py-2.5 flex items-start gap-3 cursor-pointer hover:bg-slate-50 rounded-lg -mx-2 px-2 transition-colors">
              <div className="shrink-0 w-16 text-right">
                <div className="text-[10px] uppercase font-semibold text-slate-500">
                  {relativeDate(ev.start_at)}
                </div>
                <div className="text-sm font-bold text-slate-900">{fmtHM(ev.start_at)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase"
                         style={{ background: ev.color?.bg, color: ev.color?.text,
                                  borderLeft: `3px solid ${ev.color?.border}` }}>
                    {ev.event_type_label}
                  </span>
                  <span className="text-sm font-semibold text-slate-900 truncate">{ev.title}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {ev.assigned_users?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {ev.assigned_users.slice(0, 2).map((u) => u.name || u.email).join(', ')}
                      {ev.assigned_users.length > 2 && ` +${ev.assigned_users.length - 2}`}
                    </span>
                  )}
                  {ev.location && (
                    <span className="flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{ev.location}</span>
                    </span>
                  )}
                  {(ev.client || ev.contact || ev.chantier) && (
                    <span className="text-slate-600 truncate">
                      {ev.client?.nom_societe || ev.client?.nom_prenom
                        || ev.contact?.nom_prenom
                        || ev.chantier?.nom_interne
                        || ev.chantier?.project_title}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
